import React, { useEffect, useState, useCallback } from 'react';
import S from '../../styled';
import { FLASH_TYPE_SUCCESS, FLASH_TYPE_ERROR } from '../../helpers/constants';
import { isPossiblePhoneNumber } from 'react-phone-number-input'

// Components
import Module from '../Module';

// Requests
import {
  isAuthenticated,
  updateUser
} from '../../data/requests/user';
import {
  getCountries,
  getGenders,
  getLanguages
} from '../../data/requests/utilities';

export default function MyProfileModule(props) {
  const { setStore } = props;
  const { token, t } = props.store;
  const [isMounted, setIsMounted] = useState(true);
  const [moduleData, setModuleData] = useState({});
  const [moduleIsLoading, setModuleIsLoading] = useState(false);
  const { setRenderFlashMessage } = setStore;
  const [phone, setPhone] = useState('');
  const [phoneValid, setPhoneValid] = useState(true);

  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedGender, setSelectedGender] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [shareInfo, setShareInfo] = useState(false);
  const [countriesArray, setCountriesArray] = useState([]);
  const [gendersArray, setGendersArray] = useState([]);
  const [languagesArray, setLanguagesArray] = useState([]);

  const loadModuleData = useCallback(async () => {
    if (!token) return;
    try {
      setModuleIsLoading(true);
      const data = await isAuthenticated(token);
      setModuleData(data);
      setSelectedCountry(data.country);
      setSelectedGender(data.gender);
      setSelectedLanguage(data.primary_language);
      setShareInfo(data.share_info);
      setPhone(data.phone);

      const countries = await getCountries(token);
      const arrayOfCountries = countries.json
      arrayOfCountries.splice(0, 0, {value: '', name: t('components.MyProfileModule.selectCountry')});
      setCountriesArray(arrayOfCountries)

      const genders = await getGenders(token);
      const arrayOfGenders = genders.json
      arrayOfGenders.splice(0, 0, {value: '', name: t('components.MyProfileModule.selectGender')});
      setGendersArray(arrayOfGenders);

      const languages = await getLanguages(token);
      const arrayOfLanguages = languages.json
      arrayOfLanguages.splice(0, 0, {value: '', name: t('components.MyProfileModule.selectLanguage')});
      setLanguagesArray(arrayOfLanguages);
    } catch (error) {
      console.error('loadModuleData: ', error);
    } finally {
      if (isMounted) setModuleIsLoading(false);
    }
  }, [token, isMounted]);

  useEffect(() => {
    loadModuleData();
    return () => setIsMounted(false);
  }, [loadModuleData]);

  const handleUserChange = (e) => {
    e.preventDefault();
    const { name, value, checked } = e.target;
    let fieldValue = name == 'share_info' ? checked : value;
    if (fieldValue === '') fieldValue = null;

    setModuleData((prevData) => ({
      ...prevData,
      [name]: fieldValue
    }));
  };

  const onChangeDataPhone = (value) => {
    if (value) setPhoneValid(isPossiblePhoneNumber(value));

    setPhone(value || "");
    setModuleData((prevData) => ({
      ...prevData,
      ['phone']: value || ""
    }));
  };

  const onGenderChange = (e) => {
    e.preventDefault();
    setSelectedGender(e.target.value);
    handleUserChange(e);
  };

  const onCountryChange = (e) => {
    e.preventDefault();
    setSelectedCountry(e.target.value);
    handleUserChange(e);
  };

  const onLanguageChange = (e) => {
    e.preventDefault();
    setSelectedLanguage(e.target.value);
    handleUserChange(e);
  };

  const onShareInfoChange = (e) => {
    e.preventDefault();
    setShareInfo(e.target.checked);
    handleUserChange(e);
  };

  const onNotificationSubmit = async (e) => {
    e.preventDefault();

    if (!phoneValid) return setRenderFlashMessage(
      t('components.MyProfileModule.phoneInvalid'),
      FLASH_TYPE_ERROR
    );

    try {
      const data = await updateUser(token, moduleData);
      setModuleData(data);
      setRenderFlashMessage(
        t('components.MyProfileModule.saveSuccess'),
        FLASH_TYPE_SUCCESS
      );
    } catch (error) {
      console.error('onNotificationSubmit: ', error);
      setRenderFlashMessage(
        t('components.MyProfileModule.saveError'),
        FLASH_TYPE_ERROR
      );
    }
  };

  return (
    <Module isLoading={moduleIsLoading}>
      <S.Form
        column
        justifyBetween
        gap={14}
        flex={1}
        maxWidth
        onSubmit={onNotificationSubmit}
      >
        <S.Flex column gap={10} alignStart justifyStart>
          <S.Text as="p" maxWidth marginTop={3}>
            <S.Label htmlFor="name">
              {t('shared.name')}
            </S.Label>
            <S.Input name="name" onChange={handleUserChange} value={moduleData.name || ""} />
          </S.Text>
          <S.Text as="p" maxWidth marginTop={3}>
            <S.Label htmlFor="email">
              {t('shared.emailAddress')}
            </S.Label>
            <S.Input name="email" type="email" onChange={handleUserChange} value={moduleData.email || ""} />
          </S.Text>
          <S.Text as="p" maxWidth marginTop={3}>
            <S.Label htmlFor="website">
              {t('shared.website')}
            </S.Label>
            <S.Input name="website" onChange={handleUserChange} value={moduleData.website || ""} />
          </S.Text>
          <S.Text as="p" maxWidth marginTop={3}>
            <S.Label htmlFor="discord_username">
              {t('shared.discordUsername')}
            </S.Label>
            <S.Input name="discord_username" onChange={handleUserChange} value={moduleData.discord_username || ""} />
          </S.Text>
          <S.Flex row justifyBetween alignStart>
            <S.Flex column>
              <S.Text as="p" maxWidth marginTop={3} paddingRight={10}>
                <S.Label htmlFor="phone">
                  {t('shared.phone')}
                </S.Label>
                <S.InputPhone international withCountryCallingCode={true} name="phone" onChange={onChangeDataPhone} value={phone || ""} defaultCountry="US" countryCallingCodeEditable={true}/>
              </S.Text>
              {!phoneValid && (
                <S.Text as="span" warning smallSize>
                  {t('components.MyProfileModule.phoneInvalid')}
                </S.Text>
              )}
            </S.Flex>
            <S.Flex column>
              <S.Text as="p" maxWidth marginTop={3} paddingLeft={5}>
                <S.Label htmlFor="gender" marginRight={10}>
                  {t('shared.gender')}
                </S.Label>
                <S.Select name="gender" value={selectedGender || ""} onChange={onGenderChange}>
                  {gendersArray.map((option, i) => (
                    <S.Option key={i} value={option.value} hidden={option.hidden}>
                      {option.name}
                    </S.Option>
                  ))}
                </S.Select>
              </S.Text>
            </S.Flex>
          </S.Flex>
          <S.Flex row justifyBetween>
            <S.Flex column>
              <S.Text as="p" maxWidth marginTop={3} paddingLeft={5}>
                <S.Label htmlFor="birthday">
                  {t('shared.birthday')}
                </S.Label>
                <S.Input name="birthday" type="date" onChange={handleUserChange} value={moduleData.birthday || ""} />
              </S.Text>
            </S.Flex>
          </S.Flex>
          <S.Text as="p" maxWidth marginTop={3}>
            <S.Label htmlFor="street">
              {t('shared.street')}
            </S.Label>
            <S.Input name="street" onChange={handleUserChange} value={moduleData.street || ""} />
          </S.Text>
          <S.Text as="p" maxWidth marginTop={3}>
            <S.Label htmlFor="city">
              {t('shared.city')}
            </S.Label>
            <S.Input name="city" onChange={handleUserChange} value={moduleData.city || ""} />
          </S.Text>
          <S.Flex row justifyBetween>
            <S.Flex column>
              <S.Text as="p" maxWidth marginTop={3} paddingRight={5}>
                <S.Label htmlFor="province">
                  {t('shared.state')}
                </S.Label>
                <S.Input name="province"  onChange={handleUserChange} value={moduleData.province || ""} />
              </S.Text>
            </S.Flex>
            <S.Flex column>
              <S.Text as="p" maxWidth marginTop={3} paddingLeft={5}>
                <S.Label htmlFor="postal_code">
                  {t('shared.postalCode')}
                </S.Label>
                <S.Input name="postal_code" onChange={handleUserChange} value={moduleData.postal_code || ""} />
              </S.Text>
            </S.Flex>
          </S.Flex>
          <S.Flex row justifyBetween>
            <S.Flex column>
              <S.Text as="p" maxWidth marginTop={3} paddingRight={10}>
                <S.Label htmlFor="country">
                  {t('shared.country')}
                </S.Label>
                <S.Select style={{width: '100%', maxWidth: '100%'}} name="country" value={selectedCountry || ""} onChange={onCountryChange}>
                  {countriesArray.map((option, i) => (
                    <S.Option key={i} value={option.value} hidden={option.hidden}>
                      {option.name}
                    </S.Option>
                  ))}
                </S.Select>
              </S.Text>
            </S.Flex>
          </S.Flex>
          <S.Flex row justifyBetween>
            <S.Flex column>
              <S.Text as="p" maxWidth marginTop={3} paddingRight={10}>
                <S.Label htmlFor="primary_language">
                  {t('shared.primaryLanguage')}
                </S.Label>
                <S.Select style={{width: '100%', maxWidth: '100%'}} name="primary_language" value={selectedLanguage || ""} onChange={onLanguageChange}>
                  {languagesArray.map((option, i) => (
                    <S.Option key={i} value={option.value} hidden={option.hidden}>
                      {option.name}
                    </S.Option>
                  ))}
                </S.Select>
              </S.Text>
            </S.Flex>
          </S.Flex>
          <S.Flex marginTop={8} gap={16} flex={0}>
            <S.Checkbox
              name="share_info"
              checked={shareInfo}
              onChange={onShareInfoChange}
            />
            <S.Label htmlFor="share_info">
              {t('components.MyProfileModule.shareInfo')}
            </S.Label>
          </S.Flex>
        </S.Flex>
        <S.Button type="submit">{t('shared.save')}</S.Button>
      </S.Form>
    </Module>
  )
}
